'use client';

import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useRouter } from 'next/navigation';
import { QueryClient, QueryClientProvider, useQuery, useQueryClient } from '@tanstack/react-query';
import { authInvalidatedEventName, ApiError, DEFAULT_QUERY_STALE_MS } from '@/lib/api/client';
import { authApi, type AuthMeResponse } from '@/lib/api/auth';
import { Button, Modal } from '@/ui';
import { defaultPathForRole } from '@/lib/session';
import { DialogProvider } from '@/providers/dialog-provider';
import type { AuthUser, UserRole } from '@/types/common';
import { useSoftphoneRuntime } from '@/providers/softphone-context';

const AUTH_ME_QUERY_KEY = ['auth', 'me'] as const;

async function fetchAuthMe(): Promise<AuthMeResponse> {
  return authApi.me();
}

type AuthStatus = 'loading' | 'authenticated' | 'unauthenticated' | 'error';

type AuthSessionValue = {
  status: AuthStatus;
  user: AuthUser | null;
  error: string | null;
  refresh: () => Promise<void>;
  logout: () => Promise<void>;
  showDeactivatedNotice: (message: string) => void;
};

const AuthSessionContext = createContext<AuthSessionValue | null>(null);

function isUnauthorized(error: unknown): boolean {
  return error instanceof ApiError && error.status === 401;
}

function SessionProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const softphoneRuntime = useSoftphoneRuntime();

  // Identity doesn't change mid-shift, so steady state is at most one
  // /api/auth/me per 5 minutes per visible tab (interval refetches pause while
  // the tab is hidden; a refocus only refetches once the data is stale).
  // Freshness beyond that is event-driven: any API 401 fires the
  // auth-invalidated listener below, and login/logout/impersonation go through
  // refresh()/logout() which invalidate this query explicitly.
  const authQuery = useQuery({
    queryKey: AUTH_ME_QUERY_KEY,
    queryFn: fetchAuthMe,
    retry: false,
    refetchOnWindowFocus: true,
    refetchInterval: 5 * 60_000,
    staleTime: 5 * 60_000,
    gcTime: 5 * 60_000,
  });

  const user = authQuery.data?.user ?? null;
  const status: AuthStatus = authQuery.isPending
    ? 'loading'
    : authQuery.isSuccess
    ? authQuery.data.user
      ? 'authenticated'
      : 'unauthenticated'
    : isUnauthorized(authQuery.error)
    ? 'unauthenticated'
    : 'error';

  useEffect(() => {
    if (!softphoneRuntime) {
      return;
    }
    // While impersonating, keep the softphone dormant: force a non-agent role so it
    // never registers the target's SIP extension, claims the tab lease, or streams
    // calls in the admin's browser. Impersonation must not affect call routing.
    const softphoneRole = user?.impersonatorId ? 'admin' : (user?.role ?? 'admin');
    softphoneRuntime.setRole(softphoneRole);
  }, [softphoneRuntime, user?.role, user?.impersonatorId]);

  useEffect(() => {
    function onAuthInvalidated(): void {
      void queryClient.invalidateQueries({ queryKey: AUTH_ME_QUERY_KEY });
    }

    window.addEventListener(authInvalidatedEventName(), onAuthInvalidated);
    return () => {
      window.removeEventListener(authInvalidatedEventName(), onAuthInvalidated);
    };
  }, [queryClient]);

  const refresh = useCallback(async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: AUTH_ME_QUERY_KEY });
    try {
      await queryClient.fetchQuery({
        queryKey: AUTH_ME_QUERY_KEY,
        queryFn: fetchAuthMe,
      });
    } catch {
      // Upstream hooks handle unauthenticated/error state.
    }
  }, [queryClient]);

  const logout = useCallback(async (): Promise<void> => {
    await authApi.logout();
    queryClient.removeQueries({ queryKey: AUTH_ME_QUERY_KEY });
    await queryClient.invalidateQueries({ queryKey: AUTH_ME_QUERY_KEY });
  }, [queryClient]);

  // The admin-configured notice for a deactivated account, shown once on top of
  // whatever page is up (the guards bounce to /login underneath it). It comes
  // from /auth/me for a session dropped by deactivation, and from the login
  // page for a refused sign-in.
  const [deactivatedNotice, setDeactivatedNotice] = useState<string | null>(null);
  const deactivatedMessage = authQuery.data?.deactivatedMessage ?? null;
  useEffect(() => {
    if (!deactivatedMessage) {
      return;
    }
    setDeactivatedNotice(deactivatedMessage);
    // Drop the dead cookie so the notice shows once, not on every /auth/me.
    void logout();
  }, [deactivatedMessage, logout]);

  const value = useMemo<AuthSessionValue>(
    () => ({
      status,
      user,
      error: authQuery.error instanceof Error ? authQuery.error.message : null,
      refresh,
      logout,
      showDeactivatedNotice: setDeactivatedNotice,
    }),
    [status, user, authQuery.error, refresh, logout]
  );

  return (
    <AuthSessionContext.Provider value={value}>
      {children}
      <Modal
        open={deactivatedNotice !== null}
        onClose={() => setDeactivatedNotice(null)}
        title="Account deactivated"
        size="sm"
        footer={
          <Button variant="primary" onClick={() => setDeactivatedNotice(null)}>
            OK
          </Button>
        }
      >
        <p className="text-sm text-zinc-300">{deactivatedNotice}</p>
      </Modal>
    </AuthSessionContext.Provider>
  );
}

export function AppRuntimeProvider({
  children,
  initialAuth = null,
}: {
  children: ReactNode;
  initialAuth?: AuthMeResponse | null;
}) {
  const [queryClient] = useState(() => {
    const client = new QueryClient({
      defaultOptions: {
        queries: {
          retry: 1,
          refetchOnWindowFocus: false,
          // Cache reads for 30s (instant back-nav), evict after 5 min; fresher reads set their own staleTime / refetchInterval.
          staleTime: DEFAULT_QUERY_STALE_MS,
          gcTime: 5 * 60_000,
        },
      },
    });
    if (initialAuth) {
      // /auth/me resolved by the server layout: seeds both the SSR pass and the
      // client cache, so SessionProvider starts authenticated with no fetch.
      client.setQueryData(AUTH_ME_QUERY_KEY, initialAuth);
    }
    return client;
  });

  return (
    <QueryClientProvider client={queryClient}>
      <DialogProvider>
        <SessionProvider>{children}</SessionProvider>
      </DialogProvider>
    </QueryClientProvider>
  );
}

export function useAuthSession(): AuthSessionValue {
  const context = useContext(AuthSessionContext);
  if (!context) {
    throw new Error('useAuthSession must be used inside AppRuntimeProvider');
  }
  return context;
}

type AuthRedirectOptions = {
  whenUnauthenticated?: string | null;
  whenAuthenticated?: string | ((user: AuthUser) => string) | null;
};

export function useAuthRedirect(options: AuthRedirectOptions): AuthSessionValue {
  const auth = useAuthSession();
  const router = useRouter();

  useEffect(() => {
    if (auth.status === 'loading') {
      return;
    }

    if (auth.status === 'authenticated' && auth.user && options.whenAuthenticated) {
      const target =
        typeof options.whenAuthenticated === 'function'
          ? options.whenAuthenticated(auth.user)
          : options.whenAuthenticated;
      router.replace(target);
      return;
    }

    if ((auth.status !== 'authenticated' || !auth.user) && options.whenUnauthenticated) {
      router.replace(options.whenUnauthenticated);
    }
  }, [auth.status, auth.user, options.whenAuthenticated, options.whenUnauthenticated, router]);

  return auth;
}

type UseAuthGuardOptions = {
  roles?: UserRole[];
  unauthorizedRedirect?: string;
  unauthenticatedRedirect?: string;
};

type AuthGuardResult = {
  status: AuthStatus;
  user: AuthUser | null;
  isReady: boolean;
  isAuthorized: boolean;
  error: string | null;
};
export type { AuthGuardResult };

type ReadyAuthGuardResult = AuthGuardResult & {
  status: 'authenticated';
  user: AuthUser;
  isReady: true;
  isAuthorized: true;
};

export function useAuthGuard(options: UseAuthGuardOptions = {}): AuthGuardResult {
  const { roles, unauthorizedRedirect, unauthenticatedRedirect } = options;
  const auth = useAuthSession();
  const router = useRouter();
  const activeUser = auth.user;
  const hasRoleAccess = !roles || roles.length === 0 || (activeUser ? roles.includes(activeUser.role) : false);

  const isAuthorized =
    auth.status === 'authenticated' &&
    Boolean(activeUser) &&
    hasRoleAccess;

  useEffect(() => {
    if (auth.status === 'unauthenticated') {
      router.replace(unauthenticatedRedirect || '/login');
      return;
    }

    if (auth.status === 'authenticated' && activeUser && roles && roles.length > 0 && !roles.includes(activeUser.role)) {
      router.replace(unauthorizedRedirect || defaultPathForRole(activeUser.role));
    }
  }, [auth.status, auth.user, roles, router, unauthorizedRedirect, unauthenticatedRedirect]);

  // Memoized so the result keeps a stable identity until the session actually
  // changes. Callers routinely put this in effect dependency arrays; a fresh
  // object literal every render turns `[auth]` deps into an infinite
  // fetch-render loop (one admin tab flooded the API at ~350 req/min).
  return useMemo<AuthGuardResult>(() => {
    if (auth.status === 'loading') {
      return { status: auth.status, user: null, isReady: false, isAuthorized: false, error: null };
    }
    if (!isAuthorized) {
      return { status: auth.status, user: null, isReady: false, isAuthorized: false, error: auth.error };
    }
    return { status: auth.status, user: auth.user, isReady: true, isAuthorized: true, error: auth.error };
  }, [auth.status, auth.user, auth.error, isAuthorized]);
}

export function isAuthGuardReady(auth: AuthGuardResult): auth is ReadyAuthGuardResult {
  return auth.isReady && Boolean(auth.user) && auth.isAuthorized && auth.status === 'authenticated';
}

export function authGuardRole(auth: AuthGuardResult): UserRole | null {
  return isAuthGuardReady(auth) ? auth.user.role : null;
}

export function authGuardHasRole(auth: AuthGuardResult, role: UserRole): boolean {
  return authGuardRole(auth) === role;
}
